import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Iter-311b — Public Privacy Policy.
 *
 * Required for Twilio A2P registration. Must describe what information we
 * collect, how it's used, how it's protected, and clearly state that user
 * information is NOT sold or shared with third parties for marketing.
 *
 * Route: /privacy-policy (with /privacy kept as a redirect alias).
 */
const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-200 px-4 py-12">
      <div className="max-w-3xl mx-auto space-y-6" data-testid="privacy-policy-page">
        <div>
          <h1 className="text-3xl font-bold text-white">iEvntür Privacy Policy</h1>
          <p className="text-zinc-400 text-sm mt-1">Effective 2026-07-01 · v1</p>
        </div>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">1. Overview</h2>
          <p>
            iEvntür (&quot;we&quot;, &quot;us&quot;, or &quot;the platform&quot;) provides an event-staffing
            marketplace connecting clients with talent. This policy explains
            what information we collect, how we use it, how we protect it,
            and the rights you have over your data.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">2. Information We Collect</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong className="text-zinc-100">Account information:</strong> name, email, phone number, role (talent / client / agency / admin), profile photo, address.</li>
            <li><strong className="text-zinc-100">Verification data:</strong> government-issued ID (for identity verification), background-check results, tax identification data (for talent payouts).</li>
            <li><strong className="text-zinc-100">Booking data:</strong> shift schedules, applications, invitations, ratings, reviews, event details, cost centers, hours worked.</li>
            <li><strong className="text-zinc-100">Location data:</strong> approximate coordinates during clock-in / clock-out only, used for attendance verification during active shifts. We do NOT track location outside of an active shift.</li>
            <li><strong className="text-zinc-100">Payment data:</strong> processed by third-party providers (Stripe). We store only limited transaction metadata (last four digits, transaction id, amount, status).</li>
            <li><strong className="text-zinc-100">Communications:</strong> in-app messages between talent, clients, and admins; SMS opt-in status; email preferences.</li>
            <li><strong className="text-zinc-100">Device data:</strong> IP address, browser type, device model, session tokens.</li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">3. How We Use Information</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Operate the platform (matching talent to gigs, processing bookings, calculating payments).</li>
            <li>Send transactional communications (booking confirmations, shift reminders, payment notifications, security alerts).</li>
            <li>Send SMS notifications ONLY when you have opted in — see the SMS Messaging Terms in our Terms &amp; Conditions.</li>
            <li>Verify identity and comply with legal obligations (KYC, tax reporting, background checks where applicable).</li>
            <li>Prevent fraud and abuse (duplicate accounts, ghosting, no-shows).</li>
            <li>Improve product quality (aggregated, de-identified analytics).</li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">4. Information Sharing — What We Do NOT Do</h2>
          <p className="text-emerald-300 font-medium">
            We do not sell your personal information.
            We do not share your personal information with third parties for
            their own marketing purposes.
          </p>
          <p>
            We share information ONLY with:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong className="text-zinc-100">Clients you accept work from</strong> — limited profile details required for staffing.</li>
            <li><strong className="text-zinc-100">Service providers under confidentiality</strong> — payment processing (Stripe), identity verification (Yardstik), SMS delivery (Twilio), email delivery (SendGrid), cloud hosting.</li>
            <li><strong className="text-zinc-100">Legal, tax, and safety authorities</strong> — when required by law, subpoena, or to protect the platform / others from fraud or harm.</li>
            <li><strong className="text-zinc-100">Acquirers</strong> — in the event of a merger or acquisition, with continued protection under this policy.</li>
          </ul>
          <p className="italic text-zinc-400">
            SMS opt-in consent and phone numbers are never shared with third
            parties or affiliates for promotional or marketing purposes.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">5. How We Protect Information</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>All data in transit is encrypted using TLS 1.2+.</li>
            <li>Sensitive fields (payment tokens, government IDs) are encrypted at rest.</li>
            <li>Role-based access controls restrict internal access on a need-to-know basis.</li>
            <li>Audit logs capture material data changes.</li>
            <li>Regular vulnerability scans and dependency reviews.</li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">6. Your Rights &amp; Choices</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong className="text-zinc-100">Access &amp; correction:</strong> update your profile at any time from your account settings.</li>
            <li><strong className="text-zinc-100">Deletion:</strong> request account deletion via <a className="text-amber-400 underline" href="mailto:support@ievntur.com">support@ievntur.com</a>. Certain records (tax, invoicing) may be retained as required by law.</li>
            <li><strong className="text-zinc-100">Marketing opt-out:</strong> unsubscribe from any marketing email using the link in the footer; toggle SMS off by replying <strong>STOP</strong>.</li>
            <li><strong className="text-zinc-100">Data portability:</strong> request a copy of your profile in machine-readable format.</li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">7. Retention</h2>
          <p>
            We retain your information for the duration of your active account
            plus a reasonable window for legal, tax, and dispute-resolution
            purposes (typically 7 years for financial records, 3 years for
            general account records after closure).
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">8. Children</h2>
          <p>
            iEvntür is not directed to children under 18. We do not knowingly
            collect information from minors. If you believe a minor has an
            account, contact us and we will remove it.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">9. Contact</h2>
          <p>
            Privacy questions or requests:{' '}
            <a href="mailto:support@ievntur.com" className="text-amber-400 underline">
              support@ievntur.com
            </a>
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">10. Related Documents</h2>
          <ul className="list-disc pl-6">
            <li><Link to="/terms-and-conditions" className="text-amber-400 underline" data-testid="link-terms">Terms &amp; Conditions</Link></li>
            <li><Link to="/sms-terms" className="text-amber-400 underline" data-testid="link-sms-terms">SMS Terms &amp; Conditions</Link></li>
          </ul>
        </section>

        <p className="text-xs text-zinc-500 border-t border-zinc-800 pt-4">
          We may update this Privacy Policy from time to time. Material changes
          will be notified via email or an in-app banner at least 14 days
          before taking effect.
        </p>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
